/**
 * Market Watch persistence.
 *
 * The Market Watch panel is the user's workspace. The server keeps one ordered
 * list of scrip ids per user (returned by `GET /metadata` as `marketWatchScrips`)
 * so the next login resumes exactly where the previous session left off.
 *
 * Two endpoints, because the two operations are genuinely different:
 *
 * - `updateUserMarketWatchScrips` — an **add/delete delta** `{added, removed}`.
 *   The client sends only what changed and never re-sends the whole list, so a
 *   single symbol add costs one small request however long the panel is.
 * - `reorderUserMarketWatchScrips` — the whole sequence. "Move row 2 to row 5"
 *   has no delta form, so drag-reorder is the one call that sends everything.
 *
 * Diffs are taken against `syncedScripIds` — the last list the server is known
 * to hold — rather than against the panel's previous state. A failed sync
 * therefore leaves the baseline untouched, and the next change reconciles the
 * divergence instead of compounding it. The diff is computed *inside* the queued
 * job, so changes made in quick succession are each measured against the server
 * state that actually precedes them.
 *
 * Requests are serialised (see `queue`) so two changes cannot be applied out of
 * order, and every call is fire-and-forget: a failed sync is logged but must
 * never block, throw or break the panel.
 *
 * `primeSyncedMarketWatch` resets the baseline on login and discards anything
 * still queued from the previous session.
 */
import { putMethod } from "./rest-handler";
import { toScripId } from "@store/watchlist-slice";

/** De-duplicating normaliser that preserves the given order. */
const normalize = (ids: unknown[]): number[] => {
  const seen = new Set<number>();
  const result: number[] = [];
  (ids || []).forEach((raw) => {
    const id = toScripId(raw);
    if (Number.isFinite(id) && !seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  });
  return result;
};

const sameSequence = (a: number[], b: number[]): boolean =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/** The list the server is believed to hold; every diff is measured from here. */
let syncedScripIds: number[] = [];

/** Bumped on login so work queued for a previous session is discarded. */
let generation = 0;

/** Serialised request queue — see module docs. */
let queue: Promise<void> = Promise.resolve();

const enqueue = (run: () => Promise<unknown>, what: string): void => {
  const enqueuedFor = generation;
  queue = queue
    .then(async () => {
      // Drop work queued before the most recent login.
      if (enqueuedFor === generation) await run();
    })
    .catch((error) => console.warn(`Failed to ${what}`, error));
};

/**
 * Record the server's list without sending anything.
 *
 * Called once per login with the ids returned by `/metadata` — or with the
 * watchlist template being adopted on first login. Everything afterwards is
 * diffed against this.
 */
export const primeSyncedMarketWatch = (ids: unknown[]): void => {
  generation += 1;
  syncedScripIds = normalize(ids);
};

/**
 * Make the server's list match `nextScripIds`, sending only the difference.
 *
 * Call this after *every* change to the panel — adding a symbol, removing one,
 * clearing the panel, or switching watchlist (which replaces the whole set).
 * Sends no request at all when the server already matches.
 */
export const syncMarketWatchScrips = (nextScripIds: unknown[]): void => {
  const next = normalize(nextScripIds);

  enqueue(() => {
    const current = new Set(syncedScripIds);
    const incoming = new Set(next);

    const added = next.filter((id) => !current.has(id));
    const removed = syncedScripIds.filter((id) => !incoming.has(id));

    if (added.length === 0 && removed.length === 0) {
      syncedScripIds = next;
      return Promise.resolve();
    }

    return putMethod("updateUserMarketWatchScrips", JSON.stringify({ added, removed })).then(() => {
      syncedScripIds = next;
    });
  }, "sync market watch symbols");
};

/**
 * Persist the panel's order after a drag-reorder.
 *
 * The sequence is authoritative and deliberately not a delta: a shorter list
 * would delete the symbols it omits.
 */
export const syncMarketWatchOrder = (nextScripIds: unknown[]): void => {
  const next = normalize(nextScripIds);
  if (next.length === 0) return; // an empty panel is a delta, not a reorder

  enqueue(() => {
    if (sameSequence(next, syncedScripIds)) return Promise.resolve();

    return putMethod("reorderUserMarketWatchScrips", JSON.stringify(next)).then(() => {
      syncedScripIds = next;
    });
  }, "sync market watch order");
};
